/**
 * Home Page
 * ---------
 * Sections:
 *   1. Hero          — Bold headline, subheadline, dual CTA, live feed preview
 *   2. Stats Bar     — Platform-wide numbers
 *   3. How It Works  — 3-step process
 *   4. Featured Projects — 3 project cards from the portfolio
 *   5. Latest Blog   — 3 most recent blog posts
 *   6. Join CTA      — Newsletter / sign-up call to action
 */
import { Link } from 'react-router-dom';
import AnimatedSection from '../../components/AnimatedSection/AnimatedSection';
import LiveFeed from '../../components/LiveFeed/LiveFeed';
import ProjectCard from '../../components/ProjectCard/ProjectCard';
import BlogCard from '../../components/BlogCard/BlogCard';
import { projects } from '../../data/projects';
import { blogPosts } from '../../data/blogPosts';
import styles from './Home.module.css';

/* Grab the first 3 projects and first 3 blog posts for the preview sections */
const featuredProjects = projects.slice(0, 3);
const latestPosts      = blogPosts.slice(0, 3);

/* Platform stats shown in the stats bar */
const stats = [
  { value: '2,847', label: 'Scientists documenting' },
  { value: '934',   label: 'Active projects' },
  { value: '52',    label: 'Countries represented' },
  { value: '18.4k', label: 'Research updates posted' },
];

/* How-it-works steps */
const steps = [
  {
    number: '01',
    title: 'Open a Project',
    description:
      'Create a public research space, write a plain-language description of what you\'re investigating, and set your goals. Your project page is live from the moment you create it.',
    icon: (
      <svg viewBox="0 0 40 40" fill="none">
        <rect x="6" y="8" width="28" height="24" rx="3" stroke="currentColor" strokeWidth="2"/>
        <path d="M13 16h14M13 21h10M13 26h8" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
        <circle cx="30" cy="10" r="5" fill="var(--accent-green)" opacity="0.9"/>
        <path d="M28 10l1.5 1.5L32 8" stroke="#0D0F14" strokeWidth="1.5" strokeLinecap="round"/>
      </svg>
    ),
  },
  {
    number: '02',
    title: 'Document as You Go',
    description:
      'Post updates after every experiment, run, or field session. Share raw data, images, and honest reflections — including the failures. Your timeline is public from day one.',
    icon: (
      <svg viewBox="0 0 40 40" fill="none">
        <path d="M20 6v12M14 12l6-6 6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        <rect x="8" y="22" width="24" height="12" rx="2" stroke="currentColor" strokeWidth="2"/>
        <path d="M14 28h12M14 31h8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
        <circle cx="31" cy="14" r="4" fill="var(--accent-blue)" opacity="0.9"/>
        <path d="M31 12v2.5l1.5 1" stroke="#0D0F14" strokeWidth="1.3" strokeLinecap="round"/>
      </svg>
    ),
  },
  {
    number: '03',
    title: 'Connect & Collaborate',
    description:
      'Other scientists can follow your project, leave structured comments, suggest methodology changes, or formally join your research. Science becomes a dialogue, not a monologue.',
    icon: (
      <svg viewBox="0 0 40 40" fill="none">
        <circle cx="14" cy="16" r="5" stroke="currentColor" strokeWidth="2"/>
        <circle cx="26" cy="16" r="5" stroke="currentColor" strokeWidth="2"/>
        <path d="M8 32c0-4 2.7-7 6-7h12c3.3 0 6 3 6 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
        <circle cx="32" cy="10" r="4" fill="var(--accent-green)" opacity="0.9"/>
        <path d="M30.5 10h3M32 8.5v3" stroke="#0D0F14" strokeWidth="1.5" strokeLinecap="round"/>
      </svg>
    ),
  },
];

const Home = () => (
  <main className={styles.main}>

    {/* ══════════════════════════════════════
        1. HERO SECTION
    ══════════════════════════════════════ */}
    <section className={styles.hero}>
      {/* Subtle dot-grid background pattern */}
      <div className={styles.heroBg} aria-hidden="true" />

      {/* Green radial glow behind headline */}
      <div className={styles.heroGlow} aria-hidden="true" />

      <div className={styles.heroInner}>
        {/* ── Left: text content ── */}
        <div className={styles.heroContent}>
          {/* Eyebrow label */}
          <div className={styles.heroEyebrow}>
            <span className={styles.eyebrowDot} aria-hidden="true" />
            Open science platform for young researchers
          </div>

          {/* Main headline */}
          <h1 className={styles.heroHeadline}>
            Document your research.{' '}
            <span className={styles.heroAccent}>Let the world watch it happen.</span>
          </h1>

          {/* Subheadline */}
          <p className={styles.heroSub}>
            Labora is where young scientists share their work as it happens — failures, breakthroughs, and everything in between. No hiding results, no waiting until it's perfect.
          </p>

          {/* CTA buttons */}
          <div className={styles.heroCtas}>
            <Link to="/signup" className={styles.ctaPrimary}>
              Start Documenting
              <svg viewBox="0 0 20 20" fill="none" className={styles.ctaArrow}>
                <path d="M4 10h12M10 4l6 6-6 6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </Link>
            <Link to="/portfolio" className={styles.ctaSecondary}>
              Explore Projects
            </Link>
          </div>

          {/* Stats row */}
          <div className={styles.heroStats}>
            {stats.map(({ value, label }) => (
              <div key={label} className={styles.heroStat}>
                <span className={styles.heroStatValue}>{value}</span>
                <span className={styles.heroStatLabel}>{label}</span>
              </div>
            ))}
          </div>
        </div>

        {/* ── Right: live feed ── */}
        <div className={styles.heroFeed}>
          <LiveFeed />
        </div>
      </div>
    </section>

    {/* ══════════════════════════════════════
        2. AYA KOBROSLI — FOUNDER SECTION
    ══════════════════════════════════════ */}
    <section className={styles.founderSection}>
      <div className={styles.container}>
        <AnimatedSection className={styles.founderCard}>
          {/* Left: avatar */}
          <div className={styles.founderLeft} aria-hidden="true">
            <div className={styles.founderAvatar}>
              <span className={styles.founderInitials}>AK</span>
            </div>
            <div className={styles.founderPulse} />
          </div>

          {/* Right: content */}
          <div className={styles.founderRight}>
            <span className={styles.founderEyebrow}>Meet the founder</span>
            <h2 className={styles.founderName}>Aya Kobrosli</h2>
            <span className={styles.founderTitle}>Founder &amp; Young Scientist, 17</span>
            <p className={styles.founderBio}>
              Aya has been passionate about science since she was nine years old. But the more she explored, the more she saw how closed and inaccessible science felt to people like her — young, curious, and without institutional walls to stand behind. Research was hidden behind paywalls. Failures were invisible. Young scientists had nowhere to share their work in real time. She decided to build the platform she wished had existed when she started — and that became Labora.
            </p>
            <blockquote className={styles.founderQuote}>
              "Science is better when it's shared. Not the polished version — the whole thing. The failures, the breakthroughs, the questions you don't know how to answer yet. That's where the real science lives."
            </blockquote>
            <div className={styles.founderActions}>
              <Link to="/about" className={styles.founderLink}>
                Read Aya's full story
                <svg viewBox="0 0 20 20" fill="none">
                  <path d="M4 10h12M10 4l6 6-6 6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </Link>
            </div>
          </div>
        </AnimatedSection>
      </div>
    </section>

    {/* ══════════════════════════════════════
        3. HOW IT WORKS
    ══════════════════════════════════════ */}
    <section className={styles.section}>
      <div className={styles.container}>
        <AnimatedSection className={styles.sectionHeader}>
          <span className={styles.sectionEyebrow}>How Labora works</span>
          <h2 className={styles.sectionTitle}>Science in three steps</h2>
          <p className={styles.sectionSub}>
            From first hypothesis to final result — everything documented publicly, in real time.
          </p>
        </AnimatedSection>

        <div className={styles.stepsGrid}>
          {steps.map((step, i) => (
            <AnimatedSection key={step.number} delay={i * 80} className={styles.stepCard}>
              <div className={styles.stepNumber}>{step.number}</div>
              <div className={styles.stepIconWrap} aria-hidden="true">
                {step.icon}
              </div>
              <h3 className={styles.stepTitle}>{step.title}</h3>
              <p className={styles.stepDesc}>{step.description}</p>
            </AnimatedSection>
          ))}
        </div>
      </div>
    </section>

    {/* ══════════════════════════════════════
        3. FEATURED PROJECTS
    ══════════════════════════════════════ */}
    <section className={styles.sectionDark}>
      <div className={styles.container}>
        <AnimatedSection className={styles.sectionHeader}>
          <span className={styles.sectionEyebrow}>Featured projects</span>
          <h2 className={styles.sectionTitle}>Research happening right now</h2>
          <p className={styles.sectionSub}>
            These scientists are documenting their work publicly. Follow along, comment, or reach out to collaborate.
          </p>
        </AnimatedSection>

        <div className={styles.projectsGrid}>
          {featuredProjects.map((project, i) => (
            <AnimatedSection key={project.id} delay={i * 80}>
              <ProjectCard project={project} />
            </AnimatedSection>
          ))}
        </div>

        <AnimatedSection className={styles.viewAllWrap}>
          <Link to="/portfolio" className={styles.viewAllBtn}>
            View all {projects.length} projects
            <svg viewBox="0 0 20 20" fill="none">
              <path d="M4 10h12M10 4l6 6-6 6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </Link>
        </AnimatedSection>
      </div>
    </section>

    {/* ══════════════════════════════════════
        4. LATEST BLOG POSTS
    ══════════════════════════════════════ */}
    <section className={styles.section}>
      <div className={styles.container}>
        <AnimatedSection className={styles.sectionHeader}>
          <span className={styles.sectionEyebrow}>From the community</span>
          <h2 className={styles.sectionTitle}>Stories from the lab</h2>
          <p className={styles.sectionSub}>
            Honest essays from scientists about the process of doing science — the hard parts, the surprising parts, and the rewarding parts.
          </p>
        </AnimatedSection>

        <div className={styles.blogGrid}>
          {latestPosts.map((post, i) => (
            <AnimatedSection key={post.id} delay={i * 80}>
              <BlogCard post={post} />
            </AnimatedSection>
          ))}
        </div>

        <AnimatedSection className={styles.viewAllWrap}>
          <Link to="/blog" className={styles.viewAllBtn}>
            Read all articles
            <svg viewBox="0 0 20 20" fill="none">
              <path d="M4 10h12M10 4l6 6-6 6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </Link>
        </AnimatedSection>
      </div>
    </section>

    {/* ══════════════════════════════════════
        5. JOIN CTA BANNER
    ══════════════════════════════════════ */}
    <section className={styles.ctaSection}>
      <div className={styles.ctaGlow} aria-hidden="true" />
      <div className={styles.container}>
        <AnimatedSection className={styles.ctaInner}>
          <span className={styles.ctaEyebrow}>Join 2,847 scientists</span>
          <h2 className={styles.ctaTitle}>
            Your research deserves<br />an audience from day one.
          </h2>
          <p className={styles.ctaSub}>
            Stop keeping your work hidden until it's "ready." Open your first project today — your timeline starts the moment you do.
          </p>
          <div className={styles.ctaActions}>
            <Link to="/signup" className={styles.ctaPrimary}>
              Create your first project
              <svg viewBox="0 0 20 20" fill="none" className={styles.ctaArrow}>
                <path d="M4 10h12M10 4l6 6-6 6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </Link>
            <Link to="/testimonials" className={styles.ctaSecondary}>
              Read what scientists say
            </Link>
          </div>
        </AnimatedSection>
      </div>
    </section>

  </main>
);

export default Home;
